/**
 * Auto-reports unhandled errors to the feedback API when the error rate
 * exceeds a threshold. Runs silently alongside the manual FeedbackButton.
 *
 * Uses the shared errorTrail module so console.error is intercepted
 * exactly once (previously both this hook AND FeedbackButton's
 * useConsoleErrors each overwrote console.error, doubling work and
 * creating chained wrappers).
 *
 * Thresholds:
 *  - 3+ console.error / uncaught errors within 60 s → auto-submit
 *  - 5 min cooldown between auto-submissions to prevent spam
 *  - React crashes have their own, shorter cooldown so a burst of console
 *    noise can never suppress the report for an actual white-screen crash
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { post } from '../utils/apiClient';
import { nowISO } from '../utils/dateUtils';
import { logger } from '../utils/logger';
import {
  installErrorInterceptor,
  getErrorTrailSnapshot,
  recentErrorCount,
  type ErrorEntry,
} from '../utils/errorTrail';

const ERROR_THRESHOLD = 3;
const WINDOW_MS = 60_000;
const COOLDOWN_MS = 5 * 60_000;
const CRASH_COOLDOWN_MS = 30_000;
const CHECK_INTERVAL_MS = 10_000;

let lastAutoReport = 0;
let lastCrashReport = 0;

export function useAutoErrorReporter(): void {
  const location = useLocation();
  const locationRef = useRef(location.pathname + location.search);

  useEffect(() => {
    locationRef.current = location.pathname + location.search;
  }, [location]);

  useEffect(() => {
    installErrorInterceptor();

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastAutoReport < COOLDOWN_MS) return;
      if (recentErrorCount(WINDOW_MS) < ERROR_THRESHOLD) return;

      lastAutoReport = now;
      const snapshot = getErrorTrailSnapshot();
      submitAutoReport(snapshot, locationRef.current);
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);
}

async function submitAutoReport(
  errors: ErrorEntry[],
  currentUrl: string,
  message = `Automatic bug report: ${errors.length} console errors detected in the last 60 seconds.`,
): Promise<void> {
  try {
    await post('/api/feedback', {
      feedback_type: 'Auto-Bug',
      message,
      current_url: currentUrl,
      screen_dimensions: `${window.innerWidth}x${window.innerHeight}`,
      console_errors: errors,
      metadata_extra: {
        auto_reported: true,
        error_count: errors.length,
        timestamp_local: nowISO(),
      },
    });
  } catch (err) {
    logger.error('Auto error report failed:', err);
  }
}

/**
 * Call from ErrorBoundary.componentDidCatch to auto-report React crashes.
 */
export function reportErrorBoundaryCrash(
  error: unknown,
  componentStack: string | null | undefined,
  currentUrl: string,
): void {
  const now = Date.now();
  if (now - lastCrashReport < CRASH_COOLDOWN_MS) return;
  lastCrashReport = now;
  lastAutoReport = now;

  const errorName = error instanceof Error ? error.name : typeof error;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  submitAutoReport(
    [
      {
        message: `[CRASH] ${errorMessage}`,
        source: 'ErrorBoundary',
        ts: now,
      },
      ...(errorStack
        ? [{ message: errorStack.slice(0, 500), source: 'stack', ts: now }]
        : []),
      ...(componentStack
        ? [{ message: componentStack.slice(0, 500), source: 'componentStack', ts: now }]
        : []),
    ],
    currentUrl,
    `Automatic crash report: ${errorName}: ${errorMessage} (at ${currentUrl})`,
  );
}
